/**
 * My Garage Feature
 * Manages saved vehicles in localStorage and provides header integration
 */
(function() {
    'use strict';

    if (window.__scMyGarageInitialized) return;
    window.__scMyGarageInitialized = true;
  
    // Constants
    const STORAGE_KEY = 'sc-my-garage-vehicles';
    const DEFAULT_MAX_VEHICLES = 5;
    const CUSTOM_EVENT_SAVE = 'sc-vehicle-search:save-to-garage';
    const CUSTOM_EVENT_SELECT = 'sc-my-garage:vehicle-selected';
  
    // Settings (will be populated from window object)
    let settings = {
      enabled: true,
      position: 'right',
      maxVehicles: DEFAULT_MAX_VEHICLES,
      buttonText: 'My Garage',
      buttonColor: '#000000',
      textColor: '#ffffff',
      ...window.scMyGarageSettings
    };
  
    // SVG Icons
    const GARAGE_ICON = `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M2 20h20M4 20V9l8-6 8 6v11"></path>
        <path d="M9 20V14h6v6"></path>
      </svg>
    `;
  
    const TRASH_ICON = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      </svg>
    `;
  
    const CHECK_ICON = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
    `;
  
    // LocalStorage Management
    class GarageStorage {
      static getVehicles() {
        try {
          const data = localStorage.getItem(STORAGE_KEY);
          const vehicles = data ? JSON.parse(data) : [];
          return vehicles;
        } catch (e) {
          console.error('[My Garage] Error reading from localStorage:', e);
          return [];
        }
      }
  
      static saveVehicles(vehicles) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicles));
          return true;
        } catch (e) {
          console.error('[My Garage] Error saving to localStorage:', e);
          return false;
        }
      }
  
      static addVehicle(vehicle) {
        try {
          const vehicles = this.getVehicles();
          
          // Check for duplicates
          const isDuplicate = vehicles.some(v => 
            v.make === vehicle.make &&
            v.model === vehicle.model &&
            v.year === vehicle.year &&
            v.trim === vehicle.trim
          );
  
          if (isDuplicate) {
            return { success: false, reason: 'duplicate' };
          }
  
          // Check max limit
          if (vehicles.length >= settings.maxVehicles) {
            return { success: false, reason: 'limit' };
          }
  
          // Add timestamp
          vehicle.savedAt = new Date().toISOString();
          vehicles.unshift(vehicle);
          
          const saved = this.saveVehicles(vehicles);
          return { success: saved, reason: saved ? 'success' : 'error' };
        } catch (e) {
          console.error('[My Garage] Error in addVehicle:', e);
          return { success: false, reason: 'error' };
        }
      }
  
      static removeVehicle(index) {
        try {
          const vehicles = this.getVehicles();
          vehicles.splice(index, 1);
          const result = this.saveVehicles(vehicles);
          return result;
        } catch (e) {
          console.error('[My Garage] Error in removeVehicle:', e);
          return false;
        }
      }
  
      static clearAll() {
        try {
          localStorage.removeItem(STORAGE_KEY);
          return true;
        } catch (e) {
          console.error('[My Garage] Error clearing localStorage:', e);
          return false;
        }
      }
    }
  
    // UI Components
    class MyGarageUI {
      constructor() {
        this.button = null;
        this.flydown = null;
        this.isOpen = false;
      }
  
      init() {
        if (!settings.enabled) {
          return;
        }
  
        try {
          this.injectButton();
          this.createFlydown();
          this.attachEventListeners();
          // Initialize count badge after everything is set up
          if (this.button) {
            this.updateButtonCount();
          }
        } catch (e) {
          console.error('[My Garage] Error during initialization:', e);
        }
      }
  
  
      injectButton() {
        // If auto-injection is disabled, try to find existing button
        if (settings.autoInjectHeader === false) {
          const existingButton = document.querySelector('.sc-my-garage-button');
          if (existingButton) {
            this.button = existingButton;
            // Find or create container
            let container = existingButton.closest('.sc-my-garage-container');
            if (!container) {
              // If no container, wrap the button
              container = document.createElement('div');
              container.className = 'sc-my-garage-container';
              existingButton.parentNode.insertBefore(container, existingButton);
              container.appendChild(existingButton);
            }
            this.buttonContainer = container;
            // Initialize count badge on existing button
            this.updateButtonCount();
            return;
          }
          // If no existing button found, return (button will be null)
          return;
        }
  
        // Parse header selector from settings
        const headerSelectors = settings.headerSelector 
          ? settings.headerSelector.split(',').map(s => s.trim())
          : ['header', '.header', '#shopify-section-header'];
  
        // Try to find header element
        let headerElement = null;
        for (const selector of headerSelectors) {
          headerElement = document.querySelector(selector);
          if (headerElement) {
            break;
          }
        }
  
        if (!headerElement) {
          return;
        }
  
        // Create button container
        const container = document.createElement('div');
        container.className = 'sc-my-garage-container';
        
        // Add custom class if specified
        if (settings.customClass) {
          container.classList.add(settings.customClass);
        }
        
        // Create button
        const count = GarageStorage.getVehicles().length;
        this.button = document.createElement('button');
        this.button.className = 'sc-my-garage-button';
        
        // Determine button text based on viewport size
        const isMobile = window.innerWidth < 768;
        const buttonText = (isMobile && settings.mobileButtonText) 
          ? settings.mobileButtonText 
          : settings.buttonText;
        
        this.button.innerHTML = `
          ${GARAGE_ICON}
          <span class="sc-my-garage-text">${buttonText}</span>
          ${settings.showVehicleCountBadge && count > 0 ? `<span class="sc-my-garage-count">${count}</span>` : ''}
        `;
        this.button.setAttribute('aria-label', `My Garage (${count} vehicles saved)`);
        this.button.setAttribute('aria-expanded', 'false');
        this.button.setAttribute('aria-haspopup', 'true');
        
        // Apply custom styles from settings
        this.button.style.cssText = `
          background-color: ${settings.buttonColor};
          color: ${settings.textColor};
          font-size: ${settings.fontSize}px;
          padding: ${settings.paddingVertical}px ${settings.paddingHorizontal}px;
          border-radius: ${settings.borderRadius}px;
        `;
  
        container.appendChild(this.button);
  
        // Apply container styles
        container.style.cssText = `
          display: ${settings.hideOnMobile && isMobile ? 'none' : 'inline-flex'};
          align-items: center;
          position: relative;
          margin-left: ${settings.marginLeft}px;
          margin-right: ${settings.marginRight}px;
        `;
  
        // Determine injection target and method
        let targetElement = null;
        const injectionMethod = settings.injectionMethod || 'append';
        
        // For 'before' or 'after' methods, we need a sibling element
        if ((injectionMethod === 'before' || injectionMethod === 'after') && settings.siblingSelector && settings.siblingSelector.trim() !== '') {
          const trimmedSelector = settings.siblingSelector.trim();
          
          try {
            targetElement = headerElement.querySelector(trimmedSelector);
          } catch (e) {
            console.error('[My Garage] Invalid CSS selector:', trimmedSelector, e);
          }
          
          if (!targetElement) {
            targetElement = headerElement;
          }
        } else {
          targetElement = headerElement;
        }
  
        // Perform the actual insertion based on injection method
        switch (injectionMethod) {
          case 'before':
            if (targetElement !== headerElement) {
              targetElement.parentNode.insertBefore(container, targetElement);
            } else {
              headerElement.appendChild(container);
            }
            break;
          case 'after':
            if (targetElement !== headerElement) {
              if (targetElement.nextSibling) {
                targetElement.parentNode.insertBefore(container, targetElement.nextSibling);
              } else {
                targetElement.parentNode.appendChild(container);
              }
            } else {
              headerElement.appendChild(container);
            }
            break;
          case 'prepend':
            targetElement.insertBefore(container, targetElement.firstChild);
            break;
          case 'append':
          default:
            targetElement.appendChild(container);
            break;
        }
  
        this.buttonContainer = container;
        
        // Update button text on resize if mobile text is different
        if (settings.mobileButtonText && settings.mobileButtonText !== settings.buttonText) {
          let resizeTimeout;
          window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              const isMobileNow = window.innerWidth < 768;
              const newButtonText = (isMobileNow && settings.mobileButtonText) 
                ? settings.mobileButtonText 
                : settings.buttonText;
              const textSpan = this.button.querySelector('.sc-my-garage-text');
              if (textSpan) {
                textSpan.textContent = newButtonText;
              }
              
              // Update mobile visibility
              if (settings.hideOnMobile) {
                container.style.display = isMobileNow ? 'none' : 'inline-flex';
              }
            }, 250);
          });
        }
      }
  
      createFlydown() {
        // Need either button or buttonContainer to create flydown
        if (!this.button && !this.buttonContainer) {
          return;
        }
        
        // If we have button but no container, that's okay - we can still create flydown
        if (this.button && !this.buttonContainer) {
          this.buttonContainer = this.button.parentElement;
        }
  
        this.flydown = document.createElement('div');
        this.flydown.className = 'sc-my-garage-flydown';
        this.flydown.setAttribute('role', 'menu');
        this.flydown.setAttribute('aria-label', 'My Garage Menu');
        
        // Set critical styles directly to prevent theme CSS overrides
        this.flydown.style.setProperty('position', 'fixed', 'important');
        this.flydown.style.setProperty('display', 'none', 'important');
        this.flydown.style.setProperty('z-index', '999999', 'important');
        this.flydown.style.setProperty('width', '380px', 'important');
        this.flydown.style.setProperty('max-width', '380px', 'important');
        this.flydown.style.setProperty('min-width', '380px', 'important');
        this.flydown.style.setProperty('left', 'auto', 'important');
        
        document.body.appendChild(this.flydown);
        this.renderFlydownContent();
      }
  
      positionFlydown() {
        if (!this.button || !this.flydown) return;

        const buttonRect = this.button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        
        // Force flydown to be visible temporarily to get accurate dimensions
        const wasHidden = this.flydown.style.display === 'none';
        if (wasHidden) {
          this.flydown.style.visibility = 'hidden';
          this.flydown.style.display = 'block';
        }
        
        const flydownRect = this.flydown.getBoundingClientRect();
        
        // Position flydown below the button with 8px gap
        let top = buttonRect.bottom + 8;
        
        // Always align to right edge of button (for header integration)
        // Calculate position from right edge
        const flydownWidth = 380; // Fixed width
        let right = viewportWidth - buttonRect.right;
        
        // If flydown would go off left edge, adjust
        if (buttonRect.right - flydownWidth < 16) {
          right = 16; // Keep 16px from right edge
        }
        
        // Apply positioning with !important via setProperty to override CSS !important
        this.flydown.style.setProperty('right', `${right}px`, 'important');
        this.flydown.style.setProperty('left', 'auto', 'important');
        
        // Force remove any left positioning that might be set by CSS
        this.flydown.style.removeProperty('left');
        
        if (wasHidden) {
          this.flydown.style.visibility = '';
          this.flydown.style.display = 'none';
        }
  
        // Check if flydown would go below viewport
        if (top + flydownRect.height > viewportHeight - 16) {
          // Position above button instead
          top = buttonRect.top - flydownRect.height - 8;
          
          // If still not enough space, position at top with max height
          if (top < 16) {
            top = 16;
            this.flydown.style.maxHeight = `${buttonRect.top - 32}px`;
          }
        } else {
          // Reset max height in case it was changed
          this.flydown.style.maxHeight = '400px';
        }

        this.flydown.style.setProperty('top', `${top}px`, 'important');
        
        // Re-enforce width and positioning after positioning (in case theme CSS tries to override)
        this.flydown.style.setProperty('width', '380px', 'important');
        this.flydown.style.setProperty('max-width', '380px', 'important');
        this.flydown.style.setProperty('min-width', '380px', 'important');
        this.flydown.style.setProperty('right', `${right}px`, 'important');
        this.flydown.style.setProperty('left', 'auto', 'important');
        
        // Ensure the flydown respects max-height for scrolling
        // Calculate available height based on viewport
        const availableHeight = Math.min(400, viewportHeight - top - 16);
        if (availableHeight > 0) {
          this.flydown.style.maxHeight = `${availableHeight}px`;
        }
      }
  
      renderFlydownContent() {
        if (!this.flydown) {
          return;
        }
  
        const vehicles = GarageStorage.getVehicles();
  
        if (vehicles.length === 0) {
          this.flydown.innerHTML = `
            <div class="sc-my-garage-header">
              <h2 class="sc-my-garage-title">My Garage</h2>
            </div>
            <div class="sc-my-garage-empty">
              <p>No vehicles saved yet.</p>
              <p style="font-size: 13px; margin-top: 8px; color: #999;">
                Use the vehicle search to find and save your vehicles.
              </p>
            </div>
          `;
          return;
        }
  
        let html = '<div class="sc-my-garage-header"><h2 class="sc-my-garage-title">My Garage</h2></div>';
        html += '<div class="sc-my-garage-list">';
  
        vehicles.forEach((vehicle, index) => {
          const displayName = `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
          const details = [
            vehicle.trim,
            vehicle.registration
          ].filter(Boolean).join(' • ');
  
          html += `
            <div class="sc-my-garage-vehicle" role="menuitem">
              <div class="sc-my-garage-vehicle-info">
                <div class="sc-my-garage-vehicle-name">${displayName}</div>
                ${details ? `<div class="sc-my-garage-vehicle-details">${details}</div>` : ''}
              </div>
              <div class="sc-my-garage-vehicle-actions">
                <button 
                  class="sc-my-garage-select-btn" 
                  data-index="${index}"
                  aria-label="Select ${displayName}"
                >
                  Select Vehicle
                </button>
                <button 
                  class="sc-my-garage-remove-btn" 
                  data-index="${index}"
                  aria-label="Remove ${displayName}"
                >
                  ${TRASH_ICON}
                  <span class="sc-my-garage-remove-text">Remove Vehicle</span>
                </button>
              </div>
            </div>
          `;
        });
  
        html += '</div>';
        this.flydown.innerHTML = html;
  
        // Attach event listeners to buttons
        this.flydown.querySelectorAll('.sc-my-garage-select-btn').forEach(btn => {
          btn.addEventListener('click', (e) => this.handleSelect(e));
        });
  
        this.flydown.querySelectorAll('.sc-my-garage-remove-btn').forEach(btn => {
          btn.addEventListener('click', (e) => this.handleRemove(e));
        });
      }
  
      toggleFlydown() {
        this.isOpen = !this.isOpen;
        
        if (this.isOpen) {
          this.flydown.classList.add('open');
          // Force width before positioning
          this.flydown.style.setProperty('width', '380px', 'important');
          this.flydown.style.setProperty('max-width', '380px', 'important');
          this.flydown.style.setProperty('min-width', '380px', 'important');
          this.positionFlydown();
          // Use requestAnimationFrame to ensure styles are applied after class
          requestAnimationFrame(() => {
            this.flydown.style.setProperty('display', 'block', 'important');
            this.flydown.style.setProperty('position', 'fixed', 'important');
            // Re-enforce width and positioning after display
            this.flydown.style.setProperty('width', '380px', 'important');
            this.flydown.style.setProperty('max-width', '380px', 'important');
            this.flydown.style.setProperty('min-width', '380px', 'important');
            // Re-position to ensure it's in the right place
            this.positionFlydown();
          });
          this.button.setAttribute('aria-expanded', 'true');
          // Focus first vehicle or empty state
          setTimeout(() => {
            const firstButton = this.flydown.querySelector('button');
            if (firstButton) firstButton.focus();
          }, 100);
        } else {
          this.flydown.classList.remove('open');
          this.flydown.style.display = 'none';
          this.button.setAttribute('aria-expanded', 'false');
        }
      }
  
      closeFlydown() {
        if (this.isOpen) {
          this.isOpen = false;
          this.flydown.classList.remove('open');
          this.flydown.style.display = 'none';
          this.button.setAttribute('aria-expanded', 'false');
        }
      }
  
      handleSelect(e) {
        const index = parseInt(e.currentTarget.dataset.index);
        const vehicles = GarageStorage.getVehicles();
        const vehicle = vehicles[index];
  
        if (!vehicle) {
          return;
        }
  
        // Save selected vehicle to localStorage (matching vehicle search format)
        const vehicleToSave = {
          make: vehicle.make || '',
          model: vehicle.model || '',
          trim: vehicle.trim || '',
          year: vehicle.year || '',
          cabType: vehicle.cabType || '',
          transmission: vehicle.transmission || null,
          fuelType: vehicle.fuelType || null
        };
        
        // Add registration/vrm if available
        if (vehicle.registration) {
          vehicleToSave.vrm = vehicle.registration;
        }
        
        // Create a minimal fitment object for compatibility
        const fitmentToStore = {
          make: vehicle.make || '',
          model: vehicle.model || '',
          trim: vehicle.trim || '',
          yearFrom: vehicle.year || '',
          yearTo: vehicle.year || '',
          cabType: vehicle.cabType || '',
          transmission: vehicle.transmission || null
        };
        
        try {
          localStorage.setItem('selectedVehicle', JSON.stringify(vehicleToSave));
          localStorage.setItem('selectedFitment', JSON.stringify(fitmentToStore));
        } catch (error) {
          console.error('[My Garage] Error saving selected vehicle to localStorage:', error);
        }
  
        this.closeFlydown();
  
        // Navigate directly to the stored collection URL
        if (vehicle.collectionUrl) {
          window.location.href = vehicle.collectionUrl;
        } else {
          // Fallback to all collections if no URL stored
          window.location.href = '/collections/all';
        }
      }
  
      handleRemove(e) {
        const index = parseInt(e.currentTarget.dataset.index);
        const vehicles = GarageStorage.getVehicles();
        const vehicle = vehicles[index];
  
        if (!vehicle) {
          return;
        }
  
        const displayName = `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
        
        if (GarageStorage.removeVehicle(index)) {
          this.renderFlydownContent();
          this.updateButtonCount();
          this.showToast(`${displayName} removed`, 'success');
        } else {
          this.showToast('Error removing vehicle', 'error');
        }
      }
  
      updateButtonCount() {
        if (!this.button) {
          return;
        }

        const count = GarageStorage.getVehicles().length;
        const countBubble = this.button.querySelector('.sc-my-garage-count-bubble');
        const countSpan =
          this.button.querySelector('[data-garage-count]') ||
          this.button.querySelector('.sc-my-garage-count');

        if (count > 0) {
          if (countSpan) {
            countSpan.textContent = count;
            countSpan.setAttribute('data-count', count);
          } else {
            const newBubble = document.createElement('div');
            newBubble.className = 'cart-count-bubble sc-my-garage-count-bubble';
            const newCount = document.createElement('span');
            newCount.setAttribute('aria-hidden', 'true');
            newCount.setAttribute('data-garage-count', '');
            newCount.textContent = count;
            newCount.setAttribute('data-count', count);
            newBubble.appendChild(newCount);
            this.button.appendChild(newBubble);
          }

          const bubble = this.button.querySelector('.sc-my-garage-count-bubble');
          if (bubble) {
            bubble.hidden = false;
            bubble.style.display = '';
          } else if (countSpan) {
            countSpan.style.display = 'flex';
          }
        } else {
          if (countSpan) {
            countSpan.textContent = '';
            countSpan.removeAttribute('data-count');
            countSpan.style.display = '';
          }

          const bubble = countBubble || this.button.querySelector('.sc-my-garage-count-bubble');
          if (bubble) {
            bubble.hidden = true;
            bubble.style.display = 'none';
          } else if (countSpan) {
            countSpan.style.display = 'none';
          }
        }

        this.button.setAttribute('aria-label', `My Garage (${count} vehicles saved)`);
      }
  
      showToast(message, type = 'success') {
        let toast = document.querySelector('.sc-my-garage-toast');
        
        if (!toast) {
          toast = document.createElement('div');
          toast.className = 'sc-my-garage-toast';
          document.body.appendChild(toast);
        }
  
        toast.className = `sc-my-garage-toast ${type}`;
        toast.innerHTML = `
          ${CHECK_ICON}
          <span>${message}</span>
        `;
        
        toast.classList.add('show');
  
        setTimeout(() => {
          toast.classList.remove('show');
        }, 3000);
      }
  
      attachEventListeners() {
        if (!this.button) {
          return;
        }
  
        // Button click
        this.button.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleFlydown();
        });
  
        // Click outside to close
        document.addEventListener('click', (e) => {
          if (this.isOpen && 
              !this.flydown.contains(e.target) && 
              !this.button.contains(e.target)) {
            this.closeFlydown();
          }
        });
  
        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.isOpen) {
            this.closeFlydown();
            this.button.focus();
          }
        });
  
        // Listen for save events from vehicle search widget
        document.addEventListener(CUSTOM_EVENT_SAVE, (e) => {
          this.handleSaveVehicle(e.detail);
        });
  
        // Reposition flydown on window resize or scroll
        window.addEventListener('resize', () => {
          if (this.isOpen) {
            this.positionFlydown();
          }
        });
  
        window.addEventListener('scroll', () => {
          if (this.isOpen) {
            this.positionFlydown();
          }
        }, true); // Use capture to catch scroll events on any element
      }
  
      handleSaveVehicle(vehicle) {
        try {
          const result = GarageStorage.addVehicle(vehicle);
          
          if (result.success) {
            this.renderFlydownContent();
            this.updateButtonCount();
            const displayName = `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
            this.showToast(`${displayName} saved to garage`, 'success');
          } else if (result.reason === 'duplicate') {
            this.showToast('Vehicle already in garage', 'info');
          } else if (result.reason === 'limit') {
            this.showToast(`Garage is full (max ${settings.maxVehicles} vehicles)`, 'error');
          } else {
            this.showToast('Error saving vehicle', 'error');
          }
        } catch (error) {
          this.showToast('Error saving vehicle', 'error');
        }
      }
    }
  
    // Initialize when DOM is ready
    function init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          const ui = new MyGarageUI();
          ui.init();
        });
      } else {
        const ui = new MyGarageUI();
        ui.init();
      }
    }
  
    // Expose utility functions globally for external use if needed
    window.scMyGarage = {
      addVehicle: (vehicle) => GarageStorage.addVehicle(vehicle),
      removeVehicle: (index) => GarageStorage.removeVehicle(index),
      getVehicles: () => GarageStorage.getVehicles(),
      clearAll: () => GarageStorage.clearAll()
    };
  
    init();
  })();
  